import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faCheckCircle } from '@awesome.me/kit-3f20e419a7/icons/classic/solid';
import { setScreenReaderAlert, moveFocusTo } from '../utils/Accessibility';
import styles from '../styles/Alert.module.scss';

/**
 * Shows an alert message and sets off a screen reader alert.
 * @param {object} props
 * @param {string} props.msg
 * - For displayed text and screen reader alert. Ends render and alert if empty.
 * @param {boolean} [props.fullWidth]
 * - If true, width: 100%, otherwise width: fitContent.
 * @param {boolean} [props.noFocus]
 * - If true, the alert will not receive focus when shown.
 * @param {React.ReactNode} [props.children]
 * - Renders inside the alert's text area.
 * @param {string} [props.className]
 * - Applies to extra classes to the alert.
 * @param {"success" | "error"} [props.type="error"]
 * - Styles alert and sets the icon. Defaults to error.
 */
export default function Alert({ msg, fullWidth, noFocus, children, className, type = 'error' }) {
  const id = `${type}-alert`; // max one unique ID of each alert type per page

  const typeMap = {
    success: { class: styles.success, icon: faCheckCircle },
    error: { class: styles.error, icon: faExclamationCircle },
  };

  const { class: typeClass, icon: typeIcon } = typeMap[type] || typeMap.error;

  useEffect(() => {
    if (!msg) return;
    setScreenReaderAlert(msg);
    if (!noFocus) moveFocusTo(`#${id}`);
  }, [msg, id, noFocus]);

  if (!msg) return null;
  return (
    <div
      id={id}
      className={`${styles.alert} ${typeClass} ${className} ${fullWidth ? ` ${styles.full} ` : ''}`}
    >
      <FontAwesomeIcon icon={typeIcon} className={styles.icon} />
      <div className={styles.alertText}>
        <p>{msg}</p>
        {children}
      </div>
    </div>
  );
}
